import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, ButtonBase, Card, Chip, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { FormProvider } from '../../common/hook-form'
import { FilterCard, FilterCardContent, FilterRow, FilterField, FieldLabel, ApplyButton, ResetButton } from '../../common/FilterBar'
import { CourierFilter, MerchantFilterCached } from '../../common/ProshipFormComponents'
import { useDownloadWithPolicy } from '../../common/DownloadPolicyDialog'
import { apiConstants, post } from '../../common/constants'
import { kpiCardSx, kpiRowSx, awbLinkSx } from '../../common/sharedStyles'
import { trackExport } from '../../analytics/analyticsService'
import { saveBlob, filenameFromResponse } from '../freight-profitability/freightPnlUtils'
import { useNabdCasesQuery, useNabdEscalationRowsMutation, useNabdMastersQuery, useNabdSummaryQuery } from '../../redux/ptlNabdApiSlice'
import { BUCKETS, BUCKET_LABEL, EMPTY_FILTERS, OWNER_KEYS, OWNERS, PENDING_WITH, TABS, TEAM_LABEL } from './constants'
import { Dash, LateTag, OwnerChip } from './eddBreachUi'
import { buildEscalationText, copyToClipboard, fmtD, toApiFilter } from './eddBreachUtils'
import AgeingMatrix from './components/AgeingMatrix'
import CaseDrawer from './components/CaseDrawer'
import KamMappingEditor from './components/KamMappingEditor'
import SideDrawer from '../../components/SideDrawer'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'

const FILTER_KEYS = Object.keys(EMPTY_FILTERS)

/**
 * PTL → Non-ABD Shipments → EDD Breach Attribution. Every figure on the page describes the selected tab only;
 * tiles and matrix are tab-wide (so they always sum to the tab count), the list follows the filter bar.
 * Tab, filters, clicked tile and page live in the URL so a daily-review link opens the same view.
 */
export default function EddBreachAttribution() {
  const { enqueueSnackbar } = useSnackbar()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'D' ? 'D' : 'U'
  const kpi = params.get('kpi') || ''
  const page = Number(params.get('page') || 0)
  const [size, setSize] = useState(20)
  const filters = useMemo(() => {
    const f = { ...EMPTY_FILTERS }
    FILTER_KEYS.forEach((k) => { f[k] = params.get(k) || '' })
    return f
  }, [params])

  const setUrl = (patch) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([k, v]) => (v === '' || v === null || v === undefined ? next.delete(k) : next.set(k, v)))
    setParams(next, { replace: true })
  }

  const methods = useForm({ defaultValues: filters })
  const { register, watch, reset, handleSubmit } = methods

  const { data: summary, isFetching: summaryLoading, error: summaryError } = useNabdSummaryQuery(tab)
  const apiFilter = toApiFilter(tab, filters, kpi, page, size)
  const { data, isFetching, error } = useNabdCasesQuery(apiFilter)
  const { data: masters, error: mastersError } = useNabdMastersQuery()
  const [fetchEscalation, { isLoading: copying }] = useNabdEscalationRowsMutation()
  const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
  const [exporting, setExporting] = useState(false)
  const [openId, setOpenId] = useState(params.get('case') || null)
  const [mapOpen, setMapOpen] = useState(false)

  const applyFilters = (v) => setUrl({ ...v, page: '' })
  const resetFilters = () => { reset(EMPTY_FILTERS); setUrl({ ...EMPTY_FILTERS, kpi: '', page: '' }) }
  // the other tab has no Pending with filter, and the ageing / tile / page don't carry over: clear them, and reset the
  // form to what stays in the URL so it doesn't keep showing the old values
  const changeTab = (v) => {
    reset({ ...filters, bucket: '', pendingWith: '' })
    setUrl({ tab: v, bucket: '', pendingWith: '', kpi: '', page: '' })
  }
  const pickMatrix = ({ bucket, owner }) => {
    reset({ ...filters, bucket, owner })
    setUrl({ bucket, owner, kpi: '', page: '' })
  }

  const copyEscalation = async () => {
    try {
      const rows = await fetchEscalation({ ...apiFilter, page: undefined, size: undefined }).unwrap()
      if (!rows?.length) { enqueueSnackbar('Nothing to copy', { variant: 'info' }); return }
      await copyToClipboard(buildEscalationText(rows))
      enqueueSnackbar(`Escalation summary copied, ${rows.length} shipments`, { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not copy', { variant: 'error' })
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      const res = await post(`${apiConstants.PTL_NABD}/cases/export`, { ...apiFilter, page: undefined, size: undefined }, { responseType: 'blob' })
      saveBlob(res.data, filenameFromResponse(res, `ProShip_NABD_${tab}.csv`))
      trackExport('PTL_NABD_EDD_BREACH', 'ptl/edd-breach-attribution', data?.total)
    } catch (e) {
      enqueueSnackbar('Export failed. Try again.', { variant: 'error' })
    } finally {
      setExporting(false)
    }
  }

  const closeDrawer = () => { setOpenId(null); if (params.get('case')) setUrl({ case: '' }) }

  const rows = data?.rows || []
  const columns = [
    {
      field: 'awb', headerName: 'Shipment', minWidth: 140, flex: 1, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <ButtonBase onClick={() => setOpenId(row.id)} sx={awbLinkSx}>{row.awb || row.orderNo}</ButtonBase>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.orderNo}</Typography>
        </Box>
      ),
    },
    {
      field: 'merchantName', headerName: 'Merchant', minWidth: 150, flex: 1.1, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap title={row.merchantName}>{row.merchantName || '—'}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.pickupPin} to {row.dropPin}</Typography>
        </Box>
      ),
    },
    {
      field: 'courierName', headerName: 'Carrier', minWidth: 130, flex: 1, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5 }} noWrap>{row.courierName || '—'}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{row.courierCode}</Typography>
        </Box>
      ),
    },
    {
      field: 'edd', headerName: 'EDD', minWidth: 130, flex: 0.9, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: 12.5 }}>{fmtD(row.edd)}</Typography>
          {tab === 'D' && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>delivered {fmtD(row.deliveredAt)}</Typography>}
          <LateTag breachType={row.breachType} lateDays={row.lateDays} /> <Typography component='span' sx={{ fontSize: 11, color: 'text.secondary' }}>· {row.bucket}</Typography>
        </Box>
      ),
    },
    {
      field: 'currentReasonLabel', headerName: 'Delay reason', minWidth: 220, flex: 1.8, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ whiteSpace: 'normal', py: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: row.currentReasonLabel ? 'text.primary' : 'text.disabled' }}>{row.currentReasonLabel || 'Not attributed yet'}</Typography>
          {row.replyCode && <Typography sx={{ fontSize: 11, fontWeight: 600, color: OWNERS.PROZO.color }}>handed back, raised as {row.requestLabel}</Typography>}
          {row.overridden && <Typography sx={{ fontSize: 11, fontWeight: 600, color: OWNERS.CLIENT.color }}>overridden</Typography>}
        </Box>
      ),
    },
    {
      field: 'owner', headerName: 'Attribution', minWidth: 120, flex: 0.9, sortable: false,
      renderCell: ({ row }) => <OwnerChip owner={row.owner} />,
    },
    ...(tab === 'U' ? [{
      field: 'pendingWith', headerName: 'Pending with', minWidth: 110, flex: 0.8, sortable: false,
      renderCell: ({ row }) => row.pendingWithLabel ? (
        <Box><Chip size='small' label={row.pendingWithLabel} /><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.pendingDays} day{row.pendingDays === 1 ? '' : 's'}</Typography></Box>
      ) : <Dash />,
    }] : []),
    {
      field: 'assignedMemberName', headerName: 'Owner', minWidth: 130, flex: 1, sortable: false,
      renderCell: ({ row }) => row.assignedMemberName ? (
        <Box><Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{row.assignedMemberName}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{TEAM_LABEL[row.assignedTeam]}</Typography></Box>
      ) : <Dash />,
    },
    ...(tab === 'D' ? [{
      field: 'closureStatus', headerName: 'Closure', minWidth: 130, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Chip size='small' color={row.locked ? 'success' : 'error'} variant='outlined' label={row.locked ? 'Closed' : 'Pending'} sx={{ '& .MuiChip-label': { overflow: 'visible' } }} />
          {row.locked && row.closedByName && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }} noWrap title={`Closed by ${row.closedByName}`}>by {row.closedByName}</Typography>}
        </Box>
      ),
    }] : []),
    {
      field: 'actions', headerName: 'Actions', minWidth: 140, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row }) => {
        const label = tab === 'D' ? (row.locked ? 'View record' : 'Close') : (row.owner === 'CLIENT' ? 'KAM reply' : row.owner ? 'Update' : 'Attribute')
        return <Button size='small' variant={row.locked ? 'outlined' : 'contained'} color={!row.owner && tab === 'U' ? 'error' : 'primary'}
          onClick={() => setOpenId(row.id)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>{label}</Button>
      },
    },
  ]

  const team = masters?.team || []
  const activeChips = [
    filters.bucket && { k: 'bucket', label: `Ageing: ${BUCKET_LABEL[filters.bucket]}` },
    filters.owner && { k: 'owner', label: `Attribution: ${OWNERS[filters.owner]?.short}` },
    kpi && { k: 'kpi', label: `Tile: ${OWNERS[kpi]?.short}` },
  ].filter(Boolean)

  return (
    <Box>
      <PageTitle>EDD Breach Attribution</PageTitle>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
        Every breach sits in one of four states: not attributed, Prozo, Client or External. Pick a category; every figure below describes only that category.
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => changeTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} sx={{ textTransform: 'none', fontWeight: 600 }}
            label={`${t.label} (${summary?.tabCounts?.[t.key] ?? '…'})`} />
        ))}
      </Tabs>

      {summaryError && <Alert severity='error' sx={{ mb: 2 }}>{summaryError?.data?.message || summaryError?.message}</Alert>}
      {mastersError && (
        <Alert severity='error' sx={{ mb: 2 }}>{mastersError?.data?.message || mastersError?.message || 'Could not load the reasons and the PTL team. Refresh the page.'}</Alert>
      )}

      <Box sx={{ ...kpiRowSx, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', mb: 2 }}>
        {OWNER_KEYS.map((o) => (
          <ButtonBase key={o} onClick={() => setUrl({ kpi: kpi === o ? '' : o, page: '' })} aria-pressed={kpi === o}
            sx={{ ...kpiCardSx(OWNERS[o].accent), p: 1.5, display: 'block', textAlign: 'left', bgcolor: 'background.paper',
              outline: kpi === o ? `2px solid ${OWNERS[o].color}` : 'none' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>{OWNERS[o].label}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: OWNERS[o].color }}>{summaryLoading ? '…' : (summary?.tiles?.[o] ?? 0)}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{OWNERS[o].hint}</Typography>
          </ButtonBase>
        ))}
      </Box>

      <Card sx={{ p: 2, mb: 2, boxShadow: 'none' }}>
        <AgeingMatrix tab={tab} matrix={summary?.matrix} loading={summaryLoading && !summary}
          selected={{ bucket: filters.bucket, owner: filters.owner }} onPick={pickMatrix} />
      </Card>

      <FilterCard>
        <FilterCardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(applyFilters)}>
            <FilterRow>
              <FilterField><FieldLabel>Merchant</FieldLabel><MerchantFilterCached merchantType='isPTLMerchant' placeholder='All merchants' multiple /></FilterField>
              <FilterField><FieldLabel>Courier</FieldLabel><CourierFilter filterList='B2B' placeholder='All couriers' multiple /></FilterField>
              <SelectField label='Attribution' reg={register('owner')} value={watch('owner')} options={OWNER_KEYS.map((o) => ({ value: o, label: OWNERS[o].label }))} all='All attribution' />
              {tab === 'U' && <SelectField label='Pending with' reg={register('pendingWith')} value={watch('pendingWith')} options={PENDING_WITH} all='All' />}
              <SelectField label='Assigned to' reg={register('memberId')} value={watch('memberId')} options={team.map((t) => ({ value: t.id, label: t.name }))} all='All members' />
              <SelectField label={tab === 'U' ? 'Days past EDD' : 'Days late'} reg={register('bucket')} value={watch('bucket')} options={BUCKETS.map((b) => ({ value: b, label: BUCKET_LABEL[b] }))} all='All ageing' />
              <FilterField sx={{ minWidth: 220 }}>
                <FieldLabel>AWB number(s)</FieldLabel>
                <TextField size='small' fullWidth placeholder='Search AWB, comma separated for many' {...register('q')} />
              </FilterField>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ApplyButton type='submit'>Filter</ApplyButton>
                <ResetButton onClick={resetFilters}>Reset</ResetButton>
              </Box>
            </FilterRow>
          </FormProvider>
        </FilterCardContent>
      </FilterCard>

      <Card sx={{ boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, flexWrap: 'wrap' }}>
          <Typography variant='body2' color='text.secondary'>
            {data ? (data.total === data.tabTotal ? `${data.total} shipments` : `${data.total} of ${data.tabTotal} shipments`) : '…'}
          </Typography>
          {activeChips.map((c) => (
            <Chip key={c.k} size='small' label={c.label} onDelete={() => { if (c.k !== 'kpi') reset({ ...filters, [c.k]: '' }); setUrl({ [c.k]: '', page: '' }) }} />
          ))}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button size='small' variant='outlined' startIcon={<BuildOutlinedIcon />} onClick={() => setMapOpen(true)} sx={{ textTransform: 'none' }}
              title='Merchant to KAM mapping'>KAM mapping</Button>
            <LoadingButton size='small' variant='outlined' startIcon={<ContentCopyIcon />} loading={copying} onClick={copyEscalation} sx={{ textTransform: 'none' }}>Copy escalation</LoadingButton>
            <LoadingButton size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />} loading={exporting} disabled={!data?.total}
              onClick={() => triggerDownload(exportCsv, 'PTL EDD breach attribution')} sx={{ textTransform: 'none' }}>Export CSV</LoadingButton>
          </Box>
        </Box>
        {error && <Alert severity='error' sx={{ mx: 2, mb: 2 }}>{error?.data?.message || error?.message || 'Could not load the breaches.'}</Alert>}
        {!error && (
          <ProzoDataGrid
            columns={columns}
            rows={rows}
            rowCount={data?.total || 0}
            paginationMode='server'
            pagination
            page={page}
            setPage={(p) => setUrl({ page: p || '' })}
            sizePerPage={size}
            setSizePerPage={(s) => { setSize(s); setUrl({ page: '' }) }}
            loading={isFetching}
            getRowId={(r) => r.id}
            rowHeight={64}
          />
        )}
      </Card>

      {PolicyDialog}
      <CaseDrawer open={!!openId} caseId={openId} onClose={closeDrawer} />
      <SideDrawer open={mapOpen} setOpen={setMapOpen} drawerWidth='min(640px, 100vw)' title='Merchant to KAM mapping'
        subtitle='A client attributable delay pre-selects the KAM mapped here. Team rosters live on the PTL Team Master page.'>
        <Box sx={{ px: '30px', pb: 3, width: 1 }}>
          <KamMappingEditor masters={masters} canEdit={!!masters?.canEditMasters} />
          {!masters?.canEditMasters && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>Read only. Editing needs PTL_EDD_ADMIN access.</Typography>}
        </Box>
      </SideDrawer>
    </Box>
  )
}

function SelectField({ label, reg, value, options, all }) {
  return (
    <FilterField>
      <FieldLabel>{label}</FieldLabel>
      <TextField select size='small' fullWidth value={value || ''} {...reg} SelectProps={{ displayEmpty: true }}>
        <MenuItem value=''>{all}</MenuItem>
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    </FilterField>
  )
}
